"use client";

import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";

const STORAGE_KEY = "so-origin-consent";

type Choice = "granted" | "denied";

declare global {
  interface Window {
    dataLayer?: unknown[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- сигнатура gtag вариативная по дизайну
    gtag?: (...args: any[]) => void;
  }
}

/** Прокидывает решение пользователя в Consent Mode v2. */
export function applyConsent(choice: Choice) {
  window.gtag?.("consent", "update", {
    analytics_storage: choice,
    // ad_* важны, если на объект льётся реклама: с июня 2026 ad_storage - единственный
    // гейт для связки данных GA4 и Google Ads.
    ad_storage: choice,
    ad_user_data: choice,
    ad_personalization: choice,
  });
}

/**
 * Лёгкий баннер согласия вместо стороннего CMP на сотню килобайт. До явного согласия
 * Consent Mode стоит в denied (см. стаб в layout), то есть аналитика не пишет cookie
 * и не шлёт идентификаторы: этого требуют и тайский PDPA, и GDPR для европейцев.
 */
export function ConsentBanner() {
  // Пространство именно cookieConsent: ключ consent - это текст согласия под формой.
  const t = useTranslations("cookieConsent");
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY) as Choice | null;
    if (stored === "granted" || stored === "denied") {
      applyConsent(stored);
      return;
    }
    // setState через микротаску, а не прямо в теле эффекта: иначе react-hooks ругается
    // на каскадный рендер. Поведение то же, показ баннера сдвигается на тик.
    queueMicrotask(() => setVisible(true));
  }, []);

  const choose = (choice: Choice) => {
    localStorage.setItem(STORAGE_KEY, choice);
    applyConsent(choice);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label={t("label")}
      className="fixed inset-x-0 bottom-0 z-[80] border-t border-ink/10 bg-white/95 backdrop-blur-md"
    >
      <div className="mx-auto flex max-w-[1280px] flex-col gap-3 px-[clamp(20px,4vw,40px)] py-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-[13.5px] leading-[1.5] text-muted">{t("text")}</p>
        <div className="flex flex-none gap-2">
          <button
            type="button"
            onClick={() => choose("denied")}
            className="rounded-[3px] border border-ink/[.16] px-4 py-2 text-[13px] font-medium text-ink transition-colors hover:border-ink/40"
          >
            {t("decline")}
          </button>
          <button
            type="button"
            onClick={() => choose("granted")}
            className="rounded-[3px] bg-accent px-4 py-2 text-[13px] font-medium text-white transition-colors hover:bg-ink"
          >
            {t("accept")}
          </button>
        </div>
      </div>
    </div>
  );
}
